/**
 * Main function
 * 1. registerServiceWorker: register the service worker
 * 2. initPusher: init pusher on this tab's main thread
 * 3. handleServiceWorkerClaim: handle a new service worker or a hard reload
 *
 * Pusher always runs on the browser main thread, one socket per tab. The
 * service worker stays registered for its own duties (FCM push,
 * notificationclick, fetch caching, cross-tab broadcasts).
 *
 * sendMessageToSW() therefore dispatches to two distinct targets:
 * - PUSHER_MESSAGE_TYPES  -> the main-thread pusher command dispatcher
 *                            (handleMessageFromApp.js)
 * - anything else         -> postMessage to the real service worker
 *                            (swMessageRouter.js)
 * LOGOUT is the one dual-target message: this tab unsubscribes its
 * user-bound channels, and the service worker tells the other tabs.
 *
 * The two targets are gated independently: pusher commands only wait for
 * INIT_PUSHER (the pusher config), while service worker messages wait for a
 * controller to claim this tab (or for the registration to fail, in which case
 * they are dropped). A pending or failed registration therefore never blocks
 * this tab's socket, login or subscriptions.
 */
import isMatch from 'lodash/isMatch';
import isEqual from 'lodash/isEqual';

import { setDispatchAppMessage } from './pusherController.js';
import {
  INIT_PUSHER,
  LOGOUT,
  USER_ONLINE_STATUS,
  GET_AB_TEST_TOKEN,
  SET_AUTH_HEADERS,
  SUBSCRIBE_STREAM_CHANNEL,
  UNSUBSCRIBE_STREAM_CHANNEL,
  PUSHER_MESSAGE_TYPES,
} from './AppMessageTypes.js';
import {
  STREAM_VIEWERS_UPDATED,
  STREAM_REVENUE_UPDATED,
  GIFT_PRELOAD_TRIGGERED,
  COUNTER_ADDED,
  GOAL_ADDED,
  GOAL_UPDATED,
  GOAL_METADATA_UPDATED,
  GOAL_PROGRESS_UPDATED,
  GOAL_STARTED,
  GOAL_ENDED,
  STREAM_ONLINE,
  STREAM_OFFLINE,
  SOURCE_ONLINE,
  SOURCE_OFFLINE,
  SESSION_RATING_UPDATED,
} from './PusherEvents.js';
import { PUSHER_EVENT_GOT } from './SWMessageLocalTypes.js';
import { BATCHED_PUSHER_EVENT_TYPES } from '../resource/batchPusherEvents.js';
import unRegisterServiceWorker from '../resource/unRegisterServiceWorker.js';
import { serviceWorkerLog as serviceWorkerDebug } from '../resource/debug.js';
import { messengerMessageIdKey } from '../resource/ServiceWorkerMessenger.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import mergeServiceWorkerMessages from '../resource/mergeServiceWorkerMessages.js';

const isProd = 'production' === process.env.NODE_ENV;
const isServer = typeof window === 'undefined';

// Pusher commands and service-worker-owned messages queue separately: pusher
// lives on this tab's main thread, so it must never wait for the service
// worker registration to settle.
let pendingPusherMessageQueue = [];
let pendingSWPostMessageQueue = [];
// messages coming back from the service worker, waiting for the redux dispatch
let pendingSWMessageQueue = [];
const subscribedCriticalChannelQueue = [];
const subscribedNonCriticalChannelQueue = [];

let dispatch = NULL_FUNCTION;
let pusherConfig = null;

export const hasServiceWorker =
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

const logMap = {
  default: serviceWorkerDebug,
  registerServiceWorker: serviceWorkerDebug.extend('registerServiceWorker'),
  _registerServiceWorker: serviceWorkerDebug.extend('_registerServiceWorker'),
  _markServiceWorkerUnavailable: serviceWorkerDebug.extend(
    '_markServiceWorkerUnavailable'
  ),
  handleServiceWorkerClaim: serviceWorkerDebug.extend(
    'handleServiceWorkerClaim'
  ),
  initPusher: serviceWorkerDebug.extend('initPusher'),
  _initPusher: serviceWorkerDebug.extend('_initPusher'),
  flushPendingPusherMessageQueue: serviceWorkerDebug.extend(
    'flushPendingPusherMessageQueue'
  ),
  flushPendingSWPostMessageQueue: serviceWorkerDebug.extend(
    'flushPendingSWPostMessageQueue'
  ),
  flushPendingSWMessageQueue: serviceWorkerDebug.extend(
    'flushPendingSWMessageQueue'
  ),
  flushTimeWindowMessageQueue: serviceWorkerDebug.extend(
    'flushTimeWindowMessageQueue'
  ),
  sendMessageToSW: serviceWorkerDebug.extend('sendMessageToSW'),
  resetFcmToken: serviceWorkerDebug.extend('resetFcmToken'),

  messenger: serviceWorkerDebug.extend('messenger'),
};

const serviceWorkerLog = ({ scope, messages, data }) => {
  const logger = logMap[scope] || logMap.default;
  if (data) {
    return logger(...messages, data);
  }
  return logger(...messages);
};

const state = {
  isSWRegistered: false,
  isSWRegisteredFailed: false,
  isSWClaimed: false,
  isInitPusherCompleted: false,
  isPusherConfigSetupCompleted: false,

  // Pusher commands only need this tab's socket to be configured. They must
  // not depend on the service worker registration, which can stay pending for
  // a long time (installing, updating, or a browser-level failure).
  get canDispatchPusherCommand() {
    return this.isInitPusherCompleted;
  },

  get hasSWController() {
    return hasServiceWorker && Boolean(navigator.serviceWorker.controller);
  },

  get canSendMessageToSW() {
    return (
      hasServiceWorker &&
      this.isSWRegistered &&
      !this.isSWRegisteredFailed &&
      // a registration can resolve before the new worker activates and claims
      // this tab, and an unclaimed tab has no controller to post to
      this.hasSWController
    );
  },

  // A service worker message can only leave the queue once we know its fate:
  // either a controller is claiming this tab, or there will never be one (no
  // service worker support, or the registration failed).
  get isSWMessageTargetSettled() {
    return (
      !hasServiceWorker ||
      this.isSWRegisteredFailed ||
      (this.isSWRegistered && this.hasSWController)
    );
  },

  reset() {
    this.isSWRegistered = false;
    this.isSWRegisteredFailed = false;
    this.isSWClaimed = false;
    this.isInitPusherCompleted = false;
    this.isPusherConfigSetupCompleted = false;
  },
};

export const registerServiceWorker = async () => {
  if (hasServiceWorker) {
    // refer to: https://stackoverflow.com/questions/35628243/how-to-prevent-hard-reloads-from-bypassing-the-service-worker
    // unregister service worker when there is no controller
    if (!navigator.serviceWorker.controller) {
      const registration = await navigator.serviceWorker.getRegistration();
      // sync controller again after await
      const controller = navigator.serviceWorker.controller;

      let refreshBy = null;
      if (!controller && !registration?.active) {
        // sometimes we still got 'new' on hard-reload,
        // but this case could be auto recovered anyways
        refreshBy = 'new';
      } else if (!controller && registration?.active) {
        refreshBy = 'hard-reload';
      } else if (controller && !registration?.active) {
        refreshBy = 'unknown';
      } else {
        refreshBy = 'reload';
      }

      if (refreshBy === 'hard-reload') {
        serviceWorkerLog({
          scope: 'registerServiceWorker',
          messages: ['unregister start'],
        });
        await unRegisterServiceWorker();
        serviceWorkerLog({
          scope: 'registerServiceWorker',
          messages: ['unregister done'],
        });
      }
    }

    serviceWorkerLog({
      scope: 'registerServiceWorker',
      messages: ['register service worker'],
    });
    requestIdleCallback(_registerServiceWorker);
  } else {
    serviceWorkerLog({
      scope: 'registerServiceWorker',
      messages: ['service worker unavailable'],
    });
    _markServiceWorkerUnavailable();
  }
};

export const _registerServiceWorker = async () => {
  navigator.serviceWorker
    .register(window.serviceWorkerPath, { scope: '/' })
    .then(register => {
      register.addEventListener(
        'updatefound',
        handleServiceWorkerClaim(register)
      );

      return isProd
        ? true
        : serviceWorkerLog({
            scope: '_registerServiceWorker',
            messages: ['Service worker setup.'],
            data: register,
          });
    })
    .catch(error => {
      serviceWorkerLog({
        scope: '_registerServiceWorker',
        messages: ['register service worker failed'],
        data: error,
      });
      state.isSWRegisteredFailed = true;

      return isProd
        ? null
        : serviceWorkerLog({
            scope: '_registerServiceWorker',
            messages: ['register service worker failed'],
            data: error,
          });
    })
    .finally(() => {
      serviceWorkerLog({
        scope: '_registerServiceWorker',
        messages: ['register service worker completed'],
      });
      state.isSWRegistered = true;
      flushPendingSWPostMessageQueue();
    });

  try {
    serviceWorkerLog({
      scope: '_registerServiceWorker',
      messages: ['add message event listener'],
    });
    // Start listening to Service Worker events
    navigator.serviceWorker.addEventListener('message', event => {
      if (event?.data?.[messengerMessageIdKey]) {
        // events for new pattern
        return;
      }
      handleMessageFromSW(event.data);
    });

    // On a first install or a hard reload the registration resolves before the
    // new worker activates and claims this tab. The messages the service
    // worker owns (RESTORE_REDUX, UPDATE_PUSHER_DISTINCT_ID, LOGOUT) have no
    // controller to reach until then, so send them once it takes over.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      serviceWorkerLog({
        scope: '_registerServiceWorker',
        messages: ['controllerchange'],
      });
      flushPendingSWPostMessageQueue();
    });
  } catch (error) {
    const { getSentry } = await import('../resource/sentry.js');
    const { withScope, captureException } = await getSentry();
    if (withScope && captureException) {
      withScope(scope => {
        scope.setFingerprint(['enter-app-select-pusher-path', error.name]);
        captureException(error);
      });
    }
  }
};

const _markServiceWorkerUnavailable = () => {
  serviceWorkerLog({
    scope: '_markServiceWorkerUnavailable',
    messages: ['service worker unavailable; continuing without it'],
  });
  state.isSWRegistered = true;
  flushPendingSWPostMessageQueue();
};

const handleServiceWorkerClaim = register => () => {
  serviceWorkerLog({
    scope: 'handleServiceWorkerClaim',
    messages: ['updatefound'],
  });
  const newWorker = register.installing || register.waiting || register.active;

  newWorker?.addEventListener('statechange', () => {
    // newWorker.state;
    // "installing" - the install event has fired, but not yet complete
    // "installed"  - install complete
    // "activating" - the activate event has fired, but not yet complete
    // "activated"  - fully active
    // "redundant"  - discarded. Either failed install, or it's been
    //                replaced by a newer version
    if (newWorker.state === 'activated') {
      serviceWorkerLog({
        scope: 'handleServiceWorkerClaim',
        messages: ['statechange'],
        data: newWorker.state,
      });
      state.isSWClaimed = true;
      // pusher lives on the main thread and is unaffected by the claim; only
      // the fcm token needs to follow the new service worker
      serviceWorkerLog({
        scope: 'handleServiceWorkerClaim',
        messages: ['might reset fcm token'],
      });
      resetFcmToken();
    }
  });
};

/**
 * Initialize MessageRouter, this should be called before sending any messages
 * @param {object} params
 * @param {import('redux').Dispatch} params.dispatch Dispatch function from redux store
 * @param {object} params.headers
 */
export const initPusher = async ({
  dispatch: _dispatch,
  pusherConfig: _pusherConfig,
}) => {
  const isConfigUpdated =
    pusherConfig != null &&
    (pusherConfig.appId !== _pusherConfig.appId ||
      pusherConfig.key !== _pusherConfig.key ||
      pusherConfig.wssHost !== _pusherConfig.wssHost ||
      pusherConfig.wsHost !== _pusherConfig.wsHost ||
      pusherConfig.wssPort !== _pusherConfig.wssPort ||
      !isEqual(
        pusherConfig.enabledTransports,
        _pusherConfig.enabledTransports
      ));

  if (isConfigUpdated) {
    state.isPusherConfigSetupCompleted = false;
    state.isInitPusherCompleted = false;
  }

  if (state.isPusherConfigSetupCompleted) {
    serviceWorkerLog({
      scope: 'initPusher',
      messages: ['initPusher has already being called.'],
    });
    return;
  }
  dispatch = _dispatch;
  pusherConfig = _pusherConfig;
  state.isPusherConfigSetupCompleted = true;
  serviceWorkerLog({
    scope: 'initPusher',
    messages: ['pusher config setup completed'],
  });

  flushPendingSWMessageQueue();

  _initPusher();

  if (state.isSWClaimed) {
    serviceWorkerLog({
      scope: 'initPusher',
      messages: ['might reset fcm token'],
    });
    resetFcmToken();
  }
};

const _initPusher = () => {
  // open the gate first so INIT_PUSHER dispatches straight away and stays the
  // first pusher command to reach the controller, ahead of the ones queued
  // before the config was ready
  state.isInitPusherCompleted = true;
  sendMessageToSW({
    type: INIT_PUSHER,
    payload: { pusherConfig },
  });
  serviceWorkerLog({
    scope: '_initPusher',
    messages: ['init pusher completed'],
  });
  flushPendingPusherMessageQueue();
};

/**
 * @param {object} event
 * @param {string} event.type
 * @param {object} event.payload
 */
export const sendMessageToSW = async event => {
  if (isServer) return;

  // explicit two-target dispatch: pusher commands act on this tab's socket
  // and never leave the main thread; everything else (UPDATE_SENTRY_CONFIG,
  // RESTORE_REDUX, UPDATE_PUSHER_DISTINCT_ID and the other cross-tab
  // broadcasts) is owned by the real service worker
  const isPusherCommand = PUSHER_MESSAGE_TYPES.includes(event.type);
  // LOGOUT is the one dual-target message: this tab unsubscribes its
  // user-bound channels, while the real service worker broadcasts
  // LOGOUT_FROM_SW to the other tabs
  const isSWMessage = !isPusherCommand || event.type === LOGOUT;

  const dispatchPusherCommand = () => {
    serviceWorkerLog({
      scope: 'sendMessageToSW',
      messages: ['dispatch pusher command on main thread'],
      data: { event },
    });
    _dispatchPusherCommand(event);
  };

  const postMessageToSW = () => {
    if (!state.isSWMessageTargetSettled) {
      // the registration settled but no controller took over yet: keep waiting
      // instead of dropping a cross-tab broadcast
      pendingSWPostMessageQueue.push(postMessageToSW);
      return;
    }
    if (!state.canSendMessageToSW) return;
    serviceWorkerLog({
      scope: 'sendMessageToSW',
      messages: ['send message to service worker'],
      data: { event },
    });
    _sendMessageToSW(event);
  };

  // each target waits only for what it actually needs: a pending service
  // worker registration must not hold back this tab's pusher socket
  if (isPusherCommand) {
    if (state.canDispatchPusherCommand) dispatchPusherCommand();
    else pendingPusherMessageQueue.push(dispatchPusherCommand);
  }

  if (isSWMessage) {
    if (state.isSWMessageTargetSettled) postMessageToSW();
    else pendingSWPostMessageQueue.push(postMessageToSW);
  }

  const send = () => {
    if (isPusherCommand) dispatchPusherCommand();
    if (isSWMessage) postMessageToSW();
  };

  addToSubscribedChannelQueue({ event, send });
};

const _sendMessageToSW = async event => {
  navigator.serviceWorker?.controller?.postMessage(event);
};

const _dispatchPusherCommand = async event => {
  // lazily imported so the pusher runtime isn't evaluated until the first
  // pusher command is dispatched
  const handleMessageFromApp = (await import('./handleMessageFromApp.js'))
    .default;
  handleMessageFromApp(event);
};

const flushPendingPusherMessageQueue = () => {
  if (state.canDispatchPusherCommand && pendingPusherMessageQueue.length) {
    serviceWorkerLog({
      scope: 'flushPendingPusherMessageQueue',
      messages: ['start to flush pending pusher message queue'],
      data: pendingPusherMessageQueue.length,
    });
    // swap before draining so a command queued from within a flushed one
    // isn't dropped
    const queue = pendingPusherMessageQueue;
    pendingPusherMessageQueue = [];
    queue.forEach(cb => cb());
  }
};

const flushPendingSWPostMessageQueue = () => {
  if (state.isSWMessageTargetSettled && pendingSWPostMessageQueue.length) {
    serviceWorkerLog({
      scope: 'flushPendingSWPostMessageQueue',
      messages: ['start to flush pending service worker message queue'],
      data: pendingSWPostMessageQueue.length,
    });
    const queue = pendingSWPostMessageQueue;
    pendingSWPostMessageQueue = [];
    queue.forEach(cb => cb());
  }
};

/**
 * @param {object} event
 * @param {string} event.type
 * @param {object} event.payload
 */
// Only livestream room events go through the time window: the high-frequency
// ones we batch, plus room lifecycle events that must keep their order
// against them. Everything else is handled immediately.
const TIME_WINDOW_MESSAGE_TYPES = [
  // High-frequency room events whose payloads batch (shared source of truth
  // with the service worker frame batching)
  ...BATCHED_PUSHER_EVENT_TYPES,
  // Frequent room-state updates
  STREAM_VIEWERS_UPDATED,
  STREAM_REVENUE_UPDATED,
  GIFT_PRELOAD_TRIGGERED,
  COUNTER_ADDED,
  GOAL_ADDED,
  GOAL_UPDATED,
  GOAL_METADATA_UPDATED,
  GOAL_PROGRESS_UPDATED,
  GOAL_STARTED,
  GOAL_ENDED,
  // Room lifecycle — must keep order against the events above
  STREAM_ONLINE,
  STREAM_OFFLINE,
  SOURCE_ONLINE,
  SOURCE_OFFLINE,
  SESSION_RATING_UPDATED,
  // Log message paired with every pusher event, kept in the window so it
  // won't break merging adjacency
  PUSHER_EVENT_GOT,
];

const TIME_WINDOW_MILLISECONDS = 150;
// Cap how long a flush waits for one message. A slow handler (e.g. the
// gift animation chain) keeps running in the background instead of
// blocking the rest of the queue.
const FLUSH_HANDLER_TIMEOUT_MILLISECONDS = 500;

let timeWindowMessageQueue = [];
let timeWindowTimeoutId = null;
// Flushes run one after another on this promise chain, so two flushes
// never interleave and FIFO order holds across windows.
let timeWindowFlushChainPromise = Promise.resolve();

const flushTimeWindowMessageQueue = async () => {
  timeWindowTimeoutId = null;
  const messages = mergeServiceWorkerMessages({
    messages: timeWindowMessageQueue,
  });
  timeWindowMessageQueue = [];
  for (const message of messages) {
    // Await keeps messages in FIFO order, capped by the handler timeout.
    // The catch is attached before the race so a late rejection is still
    // handled instead of becoming an unhandled rejection.
    await Promise.race([
      Promise.resolve(_handleMessageFromSW(message)).catch(error => {
        serviceWorkerLog({
          scope: 'flushTimeWindowMessageQueue',
          messages: ['handle message failed', message?.type],
          data: error,
        });
      }),
      new Promise(resolve =>
        setTimeout(resolve, FLUSH_HANDLER_TIMEOUT_MILLISECONDS)
      ),
    ]);
  }
};

let _handleMessageFromSW = null;
export const handleMessageFromSW = async event => {
  if (isServer) return;

  if (dispatch === NULL_FUNCTION) {
    pendingSWMessageQueue.push(event);
    return;
  }

  if (!_handleMessageFromSW) {
    const { handleMessageFromSW: handleMessageFromSWModule } = await import(
      './handleMessageFromSW.js'
    );
    _handleMessageFromSW = handleMessageFromSWModule(dispatch);
  }

  if (!TIME_WINDOW_MESSAGE_TYPES.includes(event?.type)) {
    _handleMessageFromSW(event);
    return;
  }

  timeWindowMessageQueue.push(event);
  if (timeWindowTimeoutId === null) {
    timeWindowTimeoutId = setTimeout(() => {
      timeWindowFlushChainPromise = timeWindowFlushChainPromise.then(
        flushTimeWindowMessageQueue
      );
    }, TIME_WINDOW_MILLISECONDS);
  }
};

const flushPendingSWMessageQueue = async () => {
  if (dispatch !== NULL_FUNCTION && pendingSWMessageQueue.length) {
    serviceWorkerLog({
      scope: 'flushPendingSWMessageQueue',
      messages: ['start flush message from service worker'],
      data: pendingSWMessageQueue.length,
    });

    if (!_handleMessageFromSW) {
      const { handleMessageFromSW: handleMessageFromSWModule } = await import(
        './handleMessageFromSW.js'
      );
      _handleMessageFromSW = handleMessageFromSWModule(dispatch);
    }

    pendingSWMessageQueue.forEach(event => _handleMessageFromSW(event));
    pendingSWMessageQueue = [];
  }
};

/**
 * @param {object} event
 * @param {string} event.type
 * @param {object} event.payload
 */
export const dispatchAppMessage = async event => {
  if (event?.[messengerMessageIdKey]) {
    // events for new pattern
    return;
  }
  // invoke handleMessageFromSW directly
  handleMessageFromSW(event);
};

// Wire the main-app dispatcher into pusherController.js. helpers.js is only
// loaded in the main app (the Service Worker never imports it), so this
// keeps pusherController.js free of `await import('./helpers.js')` and
// stops webpack from inlining the chunk filename map into the SW bundle.
setDispatchAppMessage(dispatchAppMessage);

// TODO: remote config (start)
const CRITICAL_CHANNEL_EVENTS = [
  GET_AB_TEST_TOKEN,
  SET_AUTH_HEADERS,
  SUBSCRIBE_STREAM_CHANNEL,
  UNSUBSCRIBE_STREAM_CHANNEL,
];
const NON_CRITICAL_CHANNEL_EVENTS = [USER_ONLINE_STATUS]; // and other events start with SUBSCRIBE
const MAX_NON_CRITICAL_EVENTS_NUMBER = 200;
// TODO: remote config (end)

const addToSubscribedChannelQueue = ({ event: newEvent, send }) => {
  let queue;
  let isNonCritical = false;
  const newEventType = newEvent.type?.replace('UNSUBSCRIBE', 'SUBSCRIBE');

  if (CRITICAL_CHANNEL_EVENTS.includes(newEventType))
    queue = subscribedCriticalChannelQueue;
  else if (
    NON_CRITICAL_CHANNEL_EVENTS.includes(newEventType) ||
    newEventType?.startsWith('SUBSCRIBE')
  ) {
    queue = subscribedNonCriticalChannelQueue;
    isNonCritical = true;
  }

  if (queue) {
    const duplicateIndices = queue
      .map(({ event }, index) => ({ event, index }))
      .filter(
        ({ event }) =>
          event.type === newEventType &&
          isMatch(event.payload, newEvent.payload)
      )
      .map(({ index }) => index);

    const isUnsubscribedEvent = newEvent.type?.startsWith('UNSUBSCRIBE');

    // remove the relative subscribed events of the unsubscribed event
    if (duplicateIndices.length && isUnsubscribedEvent)
      duplicateIndices.forEach(index => queue.splice(index, 1));
    // no duplicated, and is not unsubscribed event, push to queue
    else if (!duplicateIndices.length && !isUnsubscribedEvent)
      queue.push({ event: newEvent, send });

    // restrict the size of non critical events queue
    if (isNonCritical && queue.length > MAX_NON_CRITICAL_EVENTS_NUMBER)
      queue.shift();
  }
};

export const resubscribeChannels = () => {
  subscribedCriticalChannelQueue.forEach(({ send }) => send());
  subscribedNonCriticalChannelQueue.forEach(({ send }) => send());
};

const resetFcmToken = async () => {
  if (dispatch === NULL_FUNCTION) return;
  serviceWorkerLog({
    scope: 'resetFcmToken',
    messages: ['reset fcm token'],
  });

  const { default: setFcmToken } = await import('../action/setFcmToken.js');

  dispatch(setFcmToken({ via: 'new-service-worker' }));
};
